import { inject, Injectable, Provider } from "@angular/core";
import { TreeViewItem } from "projects/shared/lib/components/shared/tree-view/item/tree-view-item.model";
import { BehaviorSubject, firstValueFrom, Observable, Subscription, tap } from "rxjs";
import { ENVIRONMENT } from "projects/shared/lib/environment/environment";
import { IProductCategoryApiService, IProductCategoryApiServiceToken } from "../../api/product-category/product-category-api-service.interface";
import { LoggerService } from "../../infrastructure/logger/logger.service";
import { ServiceLocator } from "../../infrastructure/service-locator.service";
import {
  EProductCategoryImageType, ProductCategoryDetailGetQueryResult,
  ProductCategoryDto, ProductCategoryImageDto, ProductCategoryImageGetQueryResult
} from "../../INTEGRA_Shop_Client";
import { IProductCategoryService, IProductCategoryServiceToken } from "./product-category-service.interface";
import { IContextService, IContextServiceToken } from "../../infrastructure/context/context-service.interface";
import { IAssetsService, IAssetsServiceToken } from "shared/services/ui/assets/assets-service.interface";
import { IToastService, IToastServiceToken } from "shared/services/ui/toast/toast-service.interface";
import { IAuthService, IAuthServiceToken } from "shared/services/infrastructure/authentication/auth-service.interface";

@Injectable({
  providedIn: 'root'
})
export class ProductCategoryService implements IProductCategoryService {

  protected readonly logger: LoggerService;
  private readonly assetsService: IAssetsService = inject(IAssetsServiceToken);
  protected readonly toastService: IToastService = inject(IToastServiceToken);
  private readonly authService: IAuthService = inject(IAuthServiceToken);
  protected readonly productCategoryApiService: IProductCategoryApiService;
  protected readonly contextService: IContextService;

  private nestingLevelToExpand: number;

  protected categoryTreeViewSource: BehaviorSubject<TreeViewItem[] | undefined>;

  protected contextSubscription: Subscription | undefined;

  public constructor() {
    this.logger = ServiceLocator.injector.get(LoggerService);
    this.productCategoryApiService = inject(IProductCategoryApiServiceToken);
    this.contextService = inject(IContextServiceToken);

    this.nestingLevelToExpand = -1;

    let productCategoryTreeLevelToExpand = ENVIRONMENT.navSideMenu.categoryTree.levelToInitiallyExpand;
    if (productCategoryTreeLevelToExpand) {
      this.nestingLevelToExpand = productCategoryTreeLevelToExpand;
    }

    this.categoryTreeViewSource = new BehaviorSubject<TreeViewItem[] | undefined>(undefined);

    this.authSubscriptionInit();
    this.contextSubscriptionInit();
  }

  private authSubscriptionInit() {
    this.authService.customerNumberChangedGet().pipe(
      tap(() => {
        this.productCategoryTreeInitAsync();
      })
    ).subscribe();
  }

  private contextSubscriptionInit() {
    this.contextService.contextUiRefreshChangedGet()
      .subscribe(
        () => {
          this.productCategoryTreeInitAsync();
        }
      );
  }

  public categoryTreeChangedGet(): Observable<TreeViewItem[] | undefined> {
    return this.categoryTreeViewSource.asObservable();
  }

  public async productCategoryGetByCategoryKeyAsync(categoryKey: string | undefined): Promise<TreeViewItem | undefined> {
    this.logger.trace("productCategoryGetByCategoryKeyAsync has been called", this.constructor.name);

    if (this.categoryTreeViewSource.value == undefined) {
      await this.productCategoryTreeInitAsync();
    }

    let productCategoryTreeNested = this.categoryTreeViewSource.value;

    if (productCategoryTreeNested == undefined || productCategoryTreeNested.length <= 0) {
      return undefined;
    }

    let productCategory;

    if (categoryKey != undefined) {
      productCategory = this.productCategoryTreeNestedFind(categoryKey, productCategoryTreeNested);
    } else {
      productCategory = productCategoryTreeNested[0];
    }

    return productCategory;
  }

  public async productCategoryListGetByCategoryKeyAsync(categoryKey: string | undefined): Promise<TreeViewItem[]> {
    this.logger.trace("productCategoryListGetByCategoryKeyAsync has been called", this.constructor.name);
    let productCategoryTreeNested = await firstValueFrom(this.categoryTreeChangedGet());

    if (productCategoryTreeNested == undefined || productCategoryTreeNested.length <= 0) {
      return [];
    }

    if (!categoryKey) {
      return productCategoryTreeNested;
    }

    let productCategory = this.productCategoryTreeNestedFind(categoryKey, productCategoryTreeNested);

    if (productCategory == undefined) {
      return productCategoryTreeNested;
    } else {
      return [productCategory];
    }
  }

  public async productCategoryDescriptionGetByCategoryKeyAsync(categoryKey: string | undefined): Promise<string | undefined> {
    this.logger.trace("productCategoryDescriptionGetByCategoryKeyAsync has been called", this.constructor.name);
    let productCategoryTreeItem = await this.productCategoryGetByCategoryKeyAsync(categoryKey);

    if (!productCategoryTreeItem) {
      return undefined;
    }

    return productCategoryTreeItem.description;
  }

  public async productCategoryFilteringTechGroupNumberGetByCategoryKeyAsync(categoryKey: string): Promise<string | undefined> {
    this.logger.trace("productCategoryFilteringTechGroupNumberGetByCategoryKeyAsync has been called", this.constructor.name);
    let productCategoryTreeItem = await this.productCategoryGetByCategoryKeyAsync(categoryKey);

    if (!productCategoryTreeItem) {
      return undefined;
    }

    return productCategoryTreeItem.filteringTechGroupNumber;
  }

  public async productCategoryDetailGetAsync(categoryKey: string | undefined): Promise<ProductCategoryDetailGetQueryResult> {
    this.logger.trace("productCategoryDetailGetAsync has been called", this.constructor.name);
    try {
      return await firstValueFrom(this.productCategoryApiService.productCategoryDetailGet(categoryKey));
    } catch (error) {
      let dummy = error;
    }
    return new ProductCategoryDetailGetQueryResult();
  }

  public async productCategoryTreeInitAsync(): Promise<void> {
    try {
      this.logger.trace("productCategoryTreeInitAsync has been called", this.constructor.name);
      let result = await firstValueFrom(this.productCategoryApiService.productCategoryTreeGet());

      let categoryTreeList: ProductCategoryDto[] | undefined = result.productCategoryList;

      let categoryTreeNested = this.productCategoryListToTree(categoryTreeList);
      if (!categoryTreeNested) {
        categoryTreeNested = [];
      }

      this.categoryTreeViewSource.next(categoryTreeNested);
    } catch (error) {
      ServiceLocator.injector.get(LoggerService).error(error, this.constructor.name);
    }

  }

  public async productCategoryImageGetAsync(
    categoryKeyList: string[],
    productCategoryImageType: EProductCategoryImageType = EProductCategoryImageType.Tile,
    insertFallback: boolean = false,
    useCancellationLogic: boolean | undefined = true
  ): Promise<ProductCategoryImageGetQueryResult> {
    this.logger.trace("productCategoryImageGetAsync has been called", this.constructor.name);
    let productCategoryImageGetQueryResult = new ProductCategoryImageGetQueryResult({
      productCategoryImageList: []
    });

    if (categoryKeyList.length <= 0) {
      return productCategoryImageGetQueryResult;
    }

    productCategoryImageGetQueryResult = await firstValueFrom(this.productCategoryApiService.productCategoryImageGet(productCategoryImageType, categoryKeyList, useCancellationLogic));

    let catgoryDefaultImage: string = await this.assetsService.categoryDefaultImageGetAsync();
    let categoryKeyResultMissingList: string[] = [];

    let queryThumbnailList = productCategoryImageGetQueryResult.productCategoryImageList?.filter(t => t.key != undefined && t.content != undefined);

    if (insertFallback) {
      categoryKeyList.forEach(
        (categoryKey) => {
          let match = queryThumbnailList?.find(
            (resultEntry: ProductCategoryImageDto) => resultEntry.key?.localeCompare(categoryKey) == 0
          );

          if (match == undefined) {
            categoryKeyResultMissingList.push(categoryKey);
          }
        }
      );

      categoryKeyResultMissingList.forEach(
        (categoryKey) => {
          queryThumbnailList?.push(new ProductCategoryImageDto({
            key: categoryKey,
            contentType: 'image/jpg',
            content: catgoryDefaultImage,
          }));
        }
      );
    }

    productCategoryImageGetQueryResult.productCategoryImageList = queryThumbnailList;

    return productCategoryImageGetQueryResult;
  }

  private productCategoryTreeNestedFind(categoryKey: string, categoryTree: TreeViewItem[]): TreeViewItem | undefined {
    if (categoryTree.length <= 0) {
      return undefined;
    }

    let match = undefined;

    for (let i = 0; i < categoryTree.length; i++) {
      let categoryTreeItem = categoryTree[i];
      if (!match) {
        if (categoryTreeItem.key == categoryKey) {
          match = categoryTreeItem;
        } else {
          let childMatch = this.productCategoryTreeNestedFind(categoryKey, categoryTreeItem.children);

          if (childMatch) {
            match = childMatch;
          }
        }
      }
    }

    return match;
  }

  protected productCategoryListToTree(
    productCategoryList: ProductCategoryDto[] | undefined,
    nestingLevel: number = -1
  ): TreeViewItem[] | undefined {
    this.logger.trace("productCategoryListToTree has been called", this.constructor.name);

    if (!productCategoryList) {
      return;
    }
    nestingLevel++;
    let tree: TreeViewItem[] = [];

    productCategoryList.forEach(
      (productCategory: ProductCategoryDto) => {
        if (!productCategory.categoryGuid || !productCategory.categoryKey) {
          return;
        }

        tree.push(this.categoryTreeItemGetByProductCategory(productCategory, nestingLevel));
      }
    );
    return tree;
  }

  private productCountGetRecursive(productCategory: ProductCategoryDto): number {
    if (!productCategory) {
      return 0;
    }
    let productCount = productCategory.productCount;

    productCategory.subCategories?.forEach(
      (subProductCategoryDto: ProductCategoryDto) => {
        productCount += this.productCountGetRecursive(subProductCategoryDto);
      }
    );

    return productCount;
  }

  private categoryTreeItemGetByProductCategory(productCategory: ProductCategoryDto, nestingLevel: number): TreeViewItem {
    this.logger.trace("categoryTreeItemGetByProductCategory has been called", this.constructor.name);
    let categoryDescription = productCategory.categoryDescription;

    if (productCategory.isRootCategory && ENVIRONMENT.productCategory?.renameRootCategory) {
      categoryDescription = $localize`:@@ProductCategoryRootName:All categories`;
    }

    let productCount: number;

    if (ENVIRONMENT.showChildProducts) {
      productCount = this.productCountGetRecursive(productCategory);
    } else {
      productCount = productCategory.productCount;
    }

    let children: TreeViewItem[] | undefined = this.productCategoryListToTree(productCategory.subCategories, nestingLevel);
    let isBranch = children != undefined && children.length > 0;

    let categoryTreeItem: TreeViewItem = new TreeViewItem({
      guid: productCategory.categoryGuid,
      key: productCategory.categoryKey,
      description: categoryDescription,
      isRootCategory: productCategory.isRootCategory,
      children: children ? children : [],
      productCount: productCount,
      expanded: nestingLevel <= this.nestingLevelToExpand ? true : false,
      filteringTechGroupNumber: productCategory.filteringTechGroupNumber,
      selected: false,
      nestingDepth: nestingLevel,
      isBranch: isBranch,
    });

    children?.forEach(
      child => {
        child.parent = categoryTreeItem;
      }
    );

    return categoryTreeItem;
  }
}

/**
 * Prepared provider with the token to implementation mapping.
 * Reference this at the desired provider list to enable the injection of this implementation by referencing the token(interface).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const ProductCategoryServiceProvider: Provider[] = [
  { provide: IProductCategoryServiceToken, useExisting: ProductCategoryService }
];
